import { ObjectId } from 'bson';
import { toJS } from 'mobx';

import { moment } from '@feathr/hooks';
import type { ListResponse, TConstraints, TOptionalConstraint } from '@feathr/rachis';

import { TRIGGERED_EMAIL_DEFAULT_DELAY } from '../constants';
import type { EmailVerification, EmailVerifications } from '../email_verifications';
import type { Template, Templates } from '../templates';
import type { Campaign } from './campaign';
import { MailrailAutoSendEmailCampaign } from './mailrail_auto_send';
import type { IMailrailDripEmailCampaign, IMailrailDripStep } from './types';
import { CampaignClass, EDripTriggerAction } from './types';

type PartialAttrs = Partial<IMailrailDripEmailCampaign>;

/**
 * Maximum number of steps in a drip (the campaign document is step 0, so this allows
 * `MAX_DRIP_CAMPAIGN_STEPS - 1` embedded steps). The 2-step minimum is enforced in the UI.
 */
export const MAX_DRIP_CAMPAIGN_STEPS = 8;
export const DEFAULT_STEP_NAME = 'Unnamed Step';

export class MailrailDripEmailCampaign<
  T extends IMailrailDripEmailCampaign = IMailrailDripEmailCampaign,
> extends MailrailAutoSendEmailCampaign<T> {
  /**
   * Builds a placeholder embedded step seeded with a minimal set of default values.
   */
  public static getBasePlaceholderStep(): Pick<
    IMailrailDripStep,
    'delay_seconds' | 'name' | 'step_id' | 'trigger_action'
  > {
    return {
      delay_seconds: TRIGGERED_EMAIL_DEFAULT_DELAY,
      name: DEFAULT_STEP_NAME,
      step_id: new ObjectId().toHexString(),
      trigger_action: EDripTriggerAction.previous_email_delivered,
    };
  }

  public override get constraints(): TConstraints<T> {
    return {
      ...super.constraints,
      // Drips only support an enrollment (start-timed) bulk send, never a post-campaign one.
      bulk_send_at_end: {
        exclusion: {
          within: [true],
          message: '^Drip campaigns do not support a post-campaign bulk send',
        },
      },
      date_enrollment_end: {
        datetime: (value: string, attributes: PartialAttrs): TOptionalConstraint => {
          if (!value) {
            return undefined;
          }
          // Must fall strictly within (date_start, date_end) when each bound is set.
          const earliest = attributes.date_start
            ? moment.utc(attributes.date_start).add(1, 'minute').startOf('minute')
            : undefined;
          const latest = attributes.date_end
            ? moment.utc(attributes.date_end).subtract(1, 'minute').startOf('minute')
            : undefined;
          if (!earliest && !latest) {
            return undefined;
          }
          return {
            ...(earliest ? { earliest } : {}),
            ...(latest ? { latest } : {}),
            message: '^Enrollment cutoff must be after the start date and before the end date',
          };
        },
      },
      enrollment_step_name: {
        presence: {
          allowEmpty: false,
          message: "^Step name can't be blank",
        },
      },
      steps: {
        presence: {
          allowEmpty: false,
          message: `^A drip campaign requires at least two steps`,
        },
        length: {
          maximum: MAX_DRIP_CAMPAIGN_STEPS - 1,
          tooLong: `^A drip campaign can have at most ${MAX_DRIP_CAMPAIGN_STEPS} steps`,
        },
      },
    };
  }

  /**
   * Builds a placeholder embedded step seeded with the campaign's sender info and compliance contact
   * info. The user completes the remaining fields (template, sender, trigger) in the editor.
   */
  public getPlaceholderStep(): IMailrailDripStep {
    return {
      // toJS yields an independent plain copy of the observable contact_info for the step
      contact_info: toJS(this.get('contact_info')),
      from_address: this.get('from_address'),
      from_name: this.get('from_name'),
      ...MailrailDripEmailCampaign.getBasePlaceholderStep(),
    };
  }

  public override getDefaults(): Partial<T> {
    return {
      ...super.getDefaults(),
      enrollment_step_name: DEFAULT_STEP_NAME,
      exit_on_conversion: false,
      steps: [MailrailDripEmailCampaign.getBasePlaceholderStep()],
    };
  }

  public override getEmailVerifications(
    EmailVerificationsCollection: EmailVerifications,
  ): ListResponse<EmailVerification> {
    const stepEmails = (this.get('steps') ?? []).map((step) => step.from_address);
    const emails = [...new Set([this.get('from_address'), ...stepEmails])];
    return EmailVerificationsCollection.list({ filters: { email__in: emails.filter(Boolean) } });
  }

  public override getEmailVerification(
    emailVerifications: ListResponse<EmailVerification>,
  ): EmailVerification | undefined {
    if (!emailVerifications || emailVerifications.isErrored) {
      return undefined;
    }
    // Email addresses are case-insensitive; normalize both sides before matching.
    const fromAddress = this.get('from_address')?.toLowerCase();
    return emailVerifications.models.find(
      (model) => model.get('email')?.toLowerCase() === fromAddress,
    );
  }

  public override getTemplates(TemplatesCollection: Templates): Template[] {
    const stepTemplateIds = (this.get('steps') ?? []).map((step) => step.template_id);
    const templateIds = [...new Set([this.get('template_id'), ...stepTemplateIds])];
    return templateIds
      .filter((templateId): templateId is string => !!templateId)
      .map((templateId) => TemplatesCollection.get(templateId));
  }
}

// Function that allows type narrowing for Mailrail drip campaigns.
export function isMailrailDripCampaign(campaign: Campaign): campaign is MailrailDripEmailCampaign {
  return campaign.get('_cls') === CampaignClass.MailrailDrip;
}
